import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
//
import { SkeletonLoadingTable } from '@/components/skeleton';
import { Iconify, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import {
  TableHeadCustom,
  TableEmptyRows,
  TableNoData,
} from '@/components/table';
import ClientWalletTableRow from './ClientWalletTableRow';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
// API
import {
  deleteWallet,
  getStripeWalletPreview,
  getSingleUser,
  syncStripeWallets,
  type StripeWalletPreviewItem,
  updateWallet,
  type WalletUpdateDto,
} from '@/features/users/shared/api';
// hooks + utils
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  CLIENT_WALLET_TABLE_HEAD,
  applySortFilterClientCards,
} from '../../utils';

type StripeCardsSyncDialogProps = {
  open: boolean;
  hasStripeCustomer: boolean;
  previewCards: StripeWalletPreviewItem[];
  isLoading: boolean;
  isSyncing: boolean;
  onClose: VoidFunction;
  onSync: VoidFunction;
};

const getPreviewCardName = (card: StripeWalletPreviewItem) => {
  const customName = card.name?.trim();
  if (customName) return customName;

  const brand = card.brand || card.type || 'card';
  const lastFour = card.last4 ? `**** **** **** ${card.last4}` : 'Stripe card';

  return `${lastFour} (${brand})`;
};

const getPreviewActionLabel = (card: StripeWalletPreviewItem) => {
  if (card.action === 'update') {
    return card.wallet_id ? `Update #${card.wallet_id}` : 'Update';
  }

  return 'Create';
};

function StripeCardsSyncDialog({
  open,
  hasStripeCustomer,
  previewCards,
  isLoading,
  isSyncing,
  onClose,
  onSync,
}: StripeCardsSyncDialogProps) {
  const hasPreviewCards = previewCards.length > 0;

  return (
    <DialogSimple
      dialogTitle="Sync cards from Stripe"
      open={open}
      onClickButtonClose={onClose}
      onClickButtonLoading={onSync}
      loadingButton
      loadingButtonText="Sync card changes"
      disagreeButton
      disagreeButtonText="Cancel"
      disagreeButtonColor="inherit"
      isSubmitting={isSyncing}
      disableLoadingButton={!hasPreviewCards || !hasStripeCustomer || isLoading}
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ px: 3 }}>
        {!hasStripeCustomer && (
          <Alert severity="warning">
            Add a Stripe Customer ID on the client Details tab before syncing
            cards.
          </Alert>
        )}

        {isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Checking Stripe for card changes...
            </Typography>
          </Stack>
        ) : hasPreviewCards ? (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Stripe ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewCards.map((card) => (
                  <TableRow
                    key={`${card.action ?? 'create'}-${card.stripe_id}`}
                  >
                    <TableCell>{getPreviewCardName(card)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={card.action === 'update' ? 'warning' : 'success'}
                        label={getPreviewActionLabel(card)}
                      />
                    </TableCell>
                    <TableCell>{card.brand || 'N/A'}</TableCell>
                    <TableCell>{`${card.exp_month}/${card.exp_year}`}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {card.stripe_id}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">No Stripe card changes found.</Alert>
        )}
      </Stack>
    </DialogSimple>
  );
}

export default function ClientWallet() {
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const clientQueryKey = ['clients', userId];
  const stripePreviewQueryKey = ['clientStripeWalletPreview', userId];
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);

  const { data: currentClient, isLoading: isCurrentClientLoading } = useQuery({
    queryKey: clientQueryKey,
    queryFn: () => getSingleUser(Number(userId)),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching current client`);
    },
    enabled: !!userId,
  });

  const updateWalletMutation = useMutation({
    mutationFn: ({
      walletId,
      walletData,
    }: {
      walletId: number;
      walletData: WalletUpdateDto;
    }) => updateWallet(walletId, walletData),
    onSuccess: () => {
      queryClient.invalidateQueries(clientQueryKey);
      successMsg('Wallet item updated successfully');
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while updating wallet item');
    },
  });

  const deleteWalletMutation = useMutation({
    mutationFn: (walletId: number) => deleteWallet(walletId),
    onSuccess: () => {
      queryClient.invalidateQueries(clientQueryKey);
      successMsg('Wallet item deleted successfully');
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while deleting wallet item');
    },
  });

  const hasStripeCustomer = Boolean(currentClient?.stripe_customer?.trim());

  const {
    data: stripePreviewCards = [],
    isLoading: isStripePreviewLoading,
    isFetching: isStripePreviewFetching,
  } = useQuery({
    queryKey: stripePreviewQueryKey,
    queryFn: () => getStripeWalletPreview(Number(userId)),
    enabled: isSyncDialogOpen && !!userId && hasStripeCustomer,
    onError: (error) => {
      errorMsg(error, 'Something went wrong while checking Stripe cards');
    },
  });

  const syncStripeWalletsMutation = useMutation({
    mutationFn: () => syncStripeWallets(Number(userId)),
    onSuccess: () => {
      queryClient.invalidateQueries(clientQueryKey);
      queryClient.invalidateQueries(stripePreviewQueryKey);
      successMsg('Stripe cards synced successfully');
      setIsSyncDialogOpen(false);
    },
    onError: (error) => {
      errorMsg(error, 'Something went wrong while syncing Stripe cards');
    },
  });

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const [searchFieldValue, setSearchFieldValue] = useState('');

  const handleFilterSearchValue = (filterName: string) => {
    setSearchFieldValue(filterName);
    setPage(0);
  };

  const handleUpdateWallet = async (
    walletId: number,
    walletData: WalletUpdateDto
  ) => {
    await updateWalletMutation.mutateAsync({ walletId, walletData });
  };

  const handleSetDefaultWallet = (walletId: number) => {
    updateWalletMutation.mutate({
      walletId,
      walletData: { default: true },
    });
  };

  const handleDeleteWallet = async (walletId: number) => {
    await deleteWalletMutation.mutateAsync(walletId);
  };

  const handleOpenSyncDialog = () => {
    setIsSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    if (syncStripeWalletsMutation.isLoading) return;

    setIsSyncDialogOpen(false);
  };

  const dataFiltered = applySortFilterClientCards({
    tableData: currentClient?.wallet || [],
    comparator: getComparator(order, orderBy),
    searchFieldValue,
  });

  const denseHeight = dense ? 52 : 72;
  const isNotFound =
    (!dataFiltered.length && !!searchFieldValue) || !dataFiltered.length;
  return (
    <Card>
      <StripeCardsSyncDialog
        open={isSyncDialogOpen}
        hasStripeCustomer={hasStripeCustomer}
        previewCards={stripePreviewCards}
        isLoading={isStripePreviewLoading || isStripePreviewFetching}
        isSyncing={syncStripeWalletsMutation.isLoading}
        onClose={handleCloseSyncDialog}
        onSync={() => syncStripeWalletsMutation.mutate()}
      />

      {isCurrentClientLoading ? (
        <SkeletonLoadingTable />
      ) : (
        <>
          <ToolbarWithSearchAndStatus
            filterText={searchFieldValue}
            onFilterText={handleFilterSearchValue}
            labels={{ search: 'Search in wallet...' }}
            action={
              <Tooltip
                title={
                  hasStripeCustomer
                    ? ''
                    : 'Add a Stripe Customer ID on the Details tab before syncing cards.'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:sync-fill" />}
                    onClick={handleOpenSyncDialog}
                    disabled={!hasStripeCustomer}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Sync from Stripe Cards
                  </Button>
                </span>
              </Tooltip>
            }
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 980, position: 'relative' }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={CLIENT_WALLET_TABLE_HEAD}
                  rowCount={dataFiltered.length}
                  numSelected={selected.length}
                  onSort={onSort}
                />
                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((card) => (
                      <ClientWalletTableRow
                        key={card.id}
                        row={card}
                        isUpdating={
                          updateWalletMutation.isLoading &&
                          updateWalletMutation.variables?.walletId === card.id
                        }
                        isDeleting={
                          deleteWalletMutation.isLoading &&
                          deleteWalletMutation.variables === card.id
                        }
                        onUpdateRow={handleUpdateWallet}
                        onSetDefaultRow={handleSetDefaultWallet}
                        onDeleteRow={handleDeleteWallet}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      (currentClient?.wallet || []).length
                    )}
                  />

                  <TableNoData isNotFound={isNotFound} title="No cards found" />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </>
      )}
    </Card>
  );
}
